import React, { ReactNode, useState, useEffect, useRef } from 'react';
import Menu from '../components/Menu';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getUsername } from '../services/authService';
import ChatBox from '../components/ChatBox';
import { useTheme } from '../context/ThemeContext';
import { FaSun, FaMoon, FaSignOutAlt, FaBars, FaUserCircle, FaUser } from 'react-icons/fa';
import './DashboardLayout/dashboardLayout.css';
import { getUserProfile, UserProfile } from '../services/userService';

interface DashboardLayoutProps {
  children: ReactNode;
}

const DashboardLayout: React.FC<DashboardLayoutProps> = ({ children }) => {
  const [activeMenuItem, setActiveMenuItem] = useState('dashboard');
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const [userInfo, setUserInfo] = useState<UserProfile | null>(null);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isSidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const { logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const username = getUsername() || 'Người dùng';
  const sidebarRef = useRef<HTMLDivElement>(null);
  const userMenuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const fetchUserInfo = async () => {
      try {
        const userData = await getUserProfile(null);
        setUserInfo(userData);
      } catch (error) {
        console.error('Lỗi khi lấy thông tin người dùng:', error);
      }
    };
    fetchUserInfo();

    // Kiểm tra kích thước màn hình và thu gọn sidebar tự động trên mobile
    const checkScreenSize = () => {
      if (window.innerWidth < 768) {
        setSidebarCollapsed(true);
      }
    };

    // Xử lý click bên ngoài sidebar để đóng trên mobile
    const handleClickOutside = (event: MouseEvent) => {
      if (sidebarRef.current && !sidebarRef.current.contains(event.target as Node) && isMobileMenuOpen && window.innerWidth < 768) {
        setIsMobileMenuOpen(false);
      }
      
      // Đóng user menu khi click bên ngoài
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setShowUserMenu(false);
      }
    };

    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);
    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      window.removeEventListener('resize', checkScreenSize);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isMobileMenuOpen]);

  // Xây dựng thông tin họ tên hiển thị
  const getDisplayName = () => {
    if (userInfo) {
      const fullName = `${userInfo.lastName || ''} ${userInfo.firstName || ''}`.trim();
      if (fullName) return fullName;
    }
    return username; // Trở về username nếu không có thông tin họ tên
  };

  const handleLogoutClick = () => {
    setShowLogoutConfirm(true);
    setShowUserMenu(false);
  };

  const handleProfileClick = () => {
    navigate('/profile');
    setShowUserMenu(false);
  };

  const handleLogoutConfirm = () => {
    logout();
    localStorage.removeItem('token');
    localStorage.removeItem('tokenType');
    localStorage.removeItem('expiresIn');
    localStorage.removeItem('user');
    localStorage.removeItem('jwt_token');
    localStorage.removeItem('auth_type');
    localStorage.removeItem('auth_user_id');
    localStorage.removeItem('auth_username');
    localStorage.removeItem('club_member_id');
    localStorage.removeItem('auth_header');
    localStorage.removeItem('auth_token');
    localStorage.removeItem('access_token');
    console.log('Đã xóa tất cả thông tin đăng nhập khỏi localStorage');
    navigate('/login');
    setShowLogoutConfirm(false);
  };

  const handleLogoutCancel = () => {
    setShowLogoutConfirm(false);
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const toggleUserMenu = () => {
    setShowUserMenu(!showUserMenu);
  };

  const handleSidebarToggle = (isCollapsed: boolean) => {
    setSidebarCollapsed(isCollapsed);
  };

  return (
    <div className="dashboard-container">
      <div ref={sidebarRef} className={`sidebar-wrapper ${isMobileMenuOpen ? 'mobile-open' : ''}`}>
      <Menu 
        activeItem={activeMenuItem} 
          onMenuItemClick={(item) => {
            setActiveMenuItem(item);
            if (window.innerWidth < 768) {
              setIsMobileMenuOpen(false);
            }
          }}
          onSidebarToggle={handleSidebarToggle}
          initialCollapsed={isSidebarCollapsed}
      />
      </div>
      
      {isMobileMenuOpen && <div className="mobile-overlay" onClick={() => setIsMobileMenuOpen(false)}></div>}
      
      <div className={`dashboard-main ${isSidebarCollapsed ? 'sidebar-collapsed' : ''}`}>
        <header className="dashboard-header">
          <div className="header-left">
            <button 
              className="mobile-menu-toggle" 
              onClick={toggleMobileMenu}
              aria-label="Toggle mobile menu"
            >
              <FaBars />
            </button>
            <div className="welcome-text">Xin chào, {getDisplayName()}</div>
          </div>
          <div className="header-right" ref={userMenuRef}>
            <div className="user-avatar-container">
              <div 
                className="user-avatar" 
                onClick={toggleUserMenu}
                title="Tài khoản của tôi"
              >
                {userInfo && userInfo.profileImageUrl ? (
                  <img 
                    src={userInfo.profileImageUrl} 
                    alt={getDisplayName()} 
                    className="avatar-image"
                  />
                ) : (
                  <FaUserCircle className="avatar-icon" />
                )}
              </div>
              
              {showUserMenu && (
                <div className="user-dropdown-menu">
                  <div className="user-dropdown-item" onClick={handleProfileClick}>
                    <FaUser className="dropdown-icon" />
                    <span>Hồ sơ</span>
                  </div>
                  <div className="user-dropdown-item" onClick={handleLogoutClick}>
                    <FaSignOutAlt className="dropdown-icon" />
                    <span>Đăng xuất</span>
                  </div>
                </div>
              )}
            </div>
          </div>
        </header>
        
        <main className="dashboard-content">
          {children}
        </main>
        
        <footer className="dashboard-footer">
          <img 
            src="/assets/strava/power/api_logo_cptblWith_strava_horiz_black.png"
            alt="Compatible with Strava"
            style={{ height: '10px' }}
          />
        </footer>
      </div>
      
      {showLogoutConfirm && (
        <div className="logout-confirm-dialog">
          <div className={`logout-confirm-content ${theme}`}>
            <div className="dialog-header">
              <h4>Xác nhận đăng xuất</h4>
              <button 
                className="close-button" 
                onClick={handleLogoutCancel}
                title="Đóng"
              >
                &times;
              </button>
            </div>
            <div className="dialog-body">
              <p>Bạn có chắc chắn muốn đăng xuất khỏi hệ thống?</p>
            </div>
            <div className="dialog-footer">
              <button 
                className="btn btn-secondary" 
                onClick={handleLogoutCancel}
              >
                Hủy
              </button>
              <button 
                className="btn btn-danger" 
                onClick={handleLogoutConfirm}
              >
                Đăng xuất
              </button>
            </div>
          </div>
        </div>
      )}
      
      <ChatBox />
    </div>
  );
};

export default DashboardLayout; 